import { consumer, useAppContext } from '@tock/coretypes';

import * as React from 'react';
import { parse, stringify } from '@tock/query-string';
import { useLocation, useRouteMatch } from 'react-router';

import { Button, Typography } from 'omakase';
import { OfferingDetails } from 'components/business/offering/OfferingDetails';
import Anchor from 'components/Anchor';
import HideFromIOSApp from 'components/ui/HideFromIOSApp';

import {
  ACTION_BUSINESS_CLICK_RESERVATION_CTA,
  ACTION_BUSINESS_SELECT_EXPERIENCE,
  ACTION_BUSINESS_VIEW_TOCK_MENU,
  EventCustomAttributes,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import {
  ACTION_BUSINESS_OFFERING_CLICKED,
  ACTION_VIEW_MENU,
  CATEGORY_BUSINESS_PAGE,
} from 'utils/analytics/eventTypesConstants';
import { IconBell } from '@tock/icons';
import { IconBell as IconBellLegacy } from 'ui/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { css } from '@emotion/react';
import { experienceOfferingToData } from 'utils/analytics/amplitude';
import { getIsSoldOut } from 'utils/experience';
import { isOnlineOrdersType } from 'utils/offerings';
import {
  offeringConsumerDetailPageRelativeLink,
  offeringConsumerMenuPageRelativeLink,
} from '@tock/shared/utils/offerings';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useConsumerConversionAnalytics } from 'hooks/analytics/useConsumerConversionAnalytics';

type ClickContext = EventCustomAttributes['clickContext'];

interface Props {
  business: consumer.IBusinessConfig;
  callToActionHint?: string;
  offering: consumer.Offering;
  onAddToWaitlist(experienceId?: number): void;
  showWaitlist: boolean;
  listIndex: number;
}

interface MatchParams {
  businessDomain: string;
  widget?: string;
}

const Offering: React.FunctionComponent<Props> = ({
  onAddToWaitlist,
  business,
  callToActionHint,
  offering,
  showWaitlist,
  listIndex,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const sendConversionAnalytics = useConsumerConversionAnalytics();
  const location = useLocation();
  const match = useRouteMatch<MatchParams>();
  const { getPath } = useAppContext();
  const handleAddToWaitlist = (e: React.MouseEvent) => {
    e.preventDefault();
    onAddToWaitlist(offering.id);
  };
  const isSoldOut = getIsSoldOut(offering);
  const prefix = BROWSER && match.params.widget ? '/widget' : '';
  const { endDate, previewMode } = parse(location.search);
  const params = endDate ? `?${stringify({ endDate, previewMode })}` : '';
  const linkTo = getPath(
    `${prefix}${offeringConsumerDetailPageRelativeLink(offering)}${params}`
  ) as string;
  const menuLink = getPath(
    `${prefix}${offeringConsumerMenuPageRelativeLink(offering)}${params}`
  ) as string;
  const dataTestIdValue = offering.name.replace(/\W/g, '');

  const onClickTarget = (target: ClickContext) => {
    sendConversionAnalytics(ACTION_BUSINESS_CLICK_RESERVATION_CTA);

    window.analytics.sendEvent(CATEGORY_BUSINESS_PAGE, ACTION_BUSINESS_OFFERING_CLICKED);
    amplitudeSendEvent(ACTION_BUSINESS_SELECT_EXPERIENCE, {
      listIndex,
      clickContext: target,
      ...experienceOfferingToData(offering),
    });
  };

  const waitlistLabel = 'Join Notify list for';
  const accessiblePrefix = isSoldOut
    ? waitlistLabel
    : isOnlineOrdersType(offering)
      ? `Order`
      : `Book now`;
  const ariaLabel = `${accessiblePrefix} ${
    offering.name
  }, ${offering.description} (opens a dialog)`;

  return (
    <HideFromIOSApp additionalConditional={offering.isChasePayExclusive}>
      <li className="Consumer-reservation Consumer-reservation-business-list-item">
        <section id={`offering-${offering.id}`}>
          <OfferingDetails
            business={business}
            isShowingOnBusinessPage={true}
            label={ariaLabel}
            linkTo={linkTo}
            offering={offering}
          />
        </section>
        {!isSoldOut && (
          <div className="Consumer-reservationCallToAction">
            <Button
              size="medium"
              variant="primary"
              aria-label={`${
                isOnlineOrdersType(offering) ? 'Order now' : 'Book now'
              } (opens a dialog)`}
              data-testid={'offering-book-button_' + dataTestIdValue}
              component={Anchor}
              href={linkTo}
              onClick={() => onClickTarget('CTA Button')}
            >
              {isOnlineOrdersType(offering) ? 'Order now' : 'Book now'}
            </Button>
            {isOnlineOrdersType(offering) && offering.showMenuLink && (
              <Button
                size="medium"
                variant="basic"
                aria-label={`View menu for ${offering.name} (opens a dialog)`}
                component={Anchor}
                href={menuLink}
                data-testid={'offering-menu-button_' + dataTestIdValue}
                onClick={() => {
                  window.analytics.sendEvent(CATEGORY_BUSINESS_PAGE, ACTION_VIEW_MENU);
                  amplitudeSendEvent(ACTION_BUSINESS_VIEW_TOCK_MENU, {
                    listIndex,
                    ...experienceOfferingToData(offering),
                  });
                }}
                css={css`
                  margin-left: 1rem;
                `}
              >
                View menu
              </Button>
            )}
            {callToActionHint && (
              <div className="Consumer-reservationHint">
                <Typography color="inherit" component="span" variant="body02">
                  {callToActionHint}
                </Typography>
              </div>
            )}
          </div>
        )}

        {isSoldOut && (
          <div className="Consumer-reservationCallToAction">
            <Button variant="primary" size="medium" disabled>
              Sold out
            </Button>

            {showWaitlist && (
              <Button
                variant="basic"
                size="medium"
                onClick={(event) => {
                  event && handleAddToWaitlist(event);
                }}
                css={css`
                  margin-left: 1rem;
                `}
                iconStart={
                  <IconMigrationUtil legacyIcon={<IconBellLegacy />} newIcon={<IconBell />} />
                }
              >
                Notify
              </Button>
            )}
          </div>
        )}
      </li>
    </HideFromIOSApp>
  );
};

export default Offering;
